import React, { createContext, useState, useEffect, useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getHomeRoute } from './utils/getHomeRoute';
import axios from 'axios';
import '@/App.css';
import { Toaster } from '@/components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import ApplicationForm from './pages/ApplicationForm';
import AptitudeTest from './pages/AptitudeTest';
import ConfirmOrientationPage from './pages/ConfirmOrientationPage';
import LoginPage from './pages/LoginPage';
import TermsAndConditions from './pages/TermsAndConditions';

// Admin layout + pages
import AdminLayout from './pages/AdminLayout';
import AdminOverviewPage from './pages/AdminOverviewPage';
import AdminIntakesPage from './pages/AdminIntakesPage';
import AdminApplicationsPage from './pages/AdminApplicationsPage';
import AdminStudentsPage from './pages/AdminStudentsPage';
import AdminCohortsPage from './pages/AdminCohortsPage';
import ApplicationReviewPage from './pages/ApplicationReviewPage';
import SiteSettings from './pages/SiteSettings';
import AdminEmailLogsPage from './pages/AdminEmailLogsPage';
import AdminBulkEmailPage from './pages/AdminBulkEmailPage';
import AdminAuditLogsPage from './pages/AdminAuditLogsPage';
import AdminGuidePage from './pages/AdminGuidePage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminRolesPage from './pages/AdminRolesPage';
import IntakePipelinePage from './pages/IntakePipelinePage';
import FinalEvaluationPage from './pages/FinalEvaluationPage';
import AdminStudentProfilePage from './pages/AdminStudentProfilePage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AuthContext = createContext(null);

// ── Sliding session interceptors ────────────────────────────────────────────
// Decode JWT expiry without a library (JWTs are base64url — no secret needed)
function getTokenExp(token) {
  try {
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return payload.exp || 0;
  } catch { return 0; }
}

let _refreshing = false;

axios.interceptors.response.use(
  async (response) => {
    const token = localStorage.getItem('token');
    if (!token || _refreshing) return response;
    const exp = getTokenExp(token);
    const nowSec = Math.floor(Date.now() / 1000);
    // If token expires within 10 minutes, silently refresh it
    if (exp && exp - nowSec < 600) {
      _refreshing = true;
      try {
        const res = await axios.post(`${API}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        localStorage.setItem('token', res.data.access_token);
      } catch { /* ignore — will 401 on next call if truly expired */ }
      finally { _refreshing = false; }
    }
    return response;
  },
  (error) => {
    if (error?.response?.status === 401) {
      // Only redirect if we're on an admin page (not the public site or login)
      if (window.location.pathname.startsWith('/admin')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export function applyBrandingPalette(colors) {
  const root = document.documentElement;
  const map = {
    primary:        '--color-primary',
    primary_fg:     '--color-primary-fg',
    secondary:      '--color-secondary',
    secondary_fg:   '--color-secondary-fg',
    accent:         '--color-accent',
    accent_fg:      '--color-accent-fg',
    muted:          '--color-muted',
    muted_fg:       '--color-muted-fg',
    background:     '--color-background',
  };
  Object.entries(map).forEach(([key, cssVar]) => {
    if (colors[key]) root.style.setProperty(cssVar, colors[key]);
  });
}

const TRACKED_PATHS = ['/', '/apply'];

function GATracker() {
  const location = useLocation();
  useEffect(() => {
    if (!window.gtag) return;
    if (TRACKED_PATHS.includes(location.pathname)) {
      window.gtag('event', 'page_view', { page_path: location.pathname });
    }
  }, [location.pathname]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Apply saved branding palette on every load (public + admin)
  useEffect(() => {
    axios.get(`${API}/branding-config`).then(res => {
      const { active_palette_id, palettes } = res.data;
      const palette = palettes.find(p => p.id === active_palette_id);
      if (palette?.colors) applyBrandingPalette(palette.colors);
    }).catch(() => {}); // silently ignore — CSS defaults remain
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const permissionsData = localStorage.getItem('permissions');
    if (token && userData) {
      const parsedUser = JSON.parse(userData);
      if (permissionsData) {
        parsedUser.permissions = JSON.parse(permissionsData);
      }
      setUser(parsedUser);
    }
    setLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('permissions', JSON.stringify(userData.permissions || []));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    setUser(null);
  };

  const hasPermission = (perm) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    return (user.permissions || []).includes(perm);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-playfair">Loading...</div>
      </div>
    );
  }

  const adminGuard = (element) =>
    user && (user.is_super_admin || (user.permissions && user.permissions.length > 0) || user.username) ? element : <Navigate to="/login" />;

  const AdminHomeRedirect = () => <Navigate to={getHomeRoute(user)} replace />;

  return (
    <AuthContext.Provider value={{ user, login, logout, hasPermission }}>
      <div className="App">
        <BrowserRouter>
          <GATracker />
          <Routes>
            {/* ── Public routes ─────────────────────────────────────── */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/apply" element={<ApplicationForm />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/aptitude-test/:token" element={<AptitudeTest />} />
            <Route path="/confirm-orientation" element={<ConfirmOrientationPage />} />
            <Route path="/login" element={<LoginPage />} />

            {/* ── Admin routes (all share the sidebar layout) ────────── */}
            <Route path="/admin" element={adminGuard(<AdminLayout />)}>
              <Route index element={<AdminHomeRedirect />} />
              <Route path="dashboard"    element={<AdminOverviewPage />} />
              <Route path="intakes"      element={<AdminIntakesPage />} />
              <Route path="intakes/:intakeId" element={<IntakePipelinePage />} />
              <Route path="intakes/:intakeId/final-evaluation" element={<FinalEvaluationPage />} />
              <Route path="applications" element={<AdminApplicationsPage />} />
              <Route path="applications/:id" element={<ApplicationReviewPage />} />
              <Route path="students"     element={<AdminStudentsPage />} />
              <Route path="students/:studentId" element={<AdminStudentProfilePage />} />
              <Route path="cohorts"      element={<AdminCohortsPage />} />
              <Route path="settings" element={<Navigate to="/admin/settings/content" replace />} />
              <Route path="settings/:tab" element={<SiteSettings />} />
              <Route path="email-logs"  element={<AdminEmailLogsPage />} />
              <Route path="bulk-email"  element={<AdminBulkEmailPage />} />
              <Route path="audit-logs"  element={<AdminAuditLogsPage />} />
              <Route path="guide"       element={<AdminGuidePage />} />
              <Route path="users"       element={<AdminUsersPage />} />
              <Route path="roles"       element={<AdminRolesPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
